import { useMemo } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, MapPin } from "lucide-react";
import type { Job, Contractor } from "@/data/types";

export interface BranchRegionState {
  branch: string;
  region: string;
}

export const EMPTY_BR: BranchRegionState = { branch: "all", region: "all" };

interface BranchRegionFilterProps {
  value: BranchRegionState;
  onChange: (value: BranchRegionState) => void;
  jobs?: Job[];
  contractors?: Contractor[];
  branches?: string[];
  regions?: string[];
  compact?: boolean;
}

export function useBranchRegionOptions(jobs: Job[] = [], contractors: Contractor[] = []) {
  return useMemo(() => {
    const branches = new Set<string>();
    const regions = new Set<string>();
    jobs.forEach((j) => {
      if (j.branch) branches.add(j.branch);
      if (j.region) regions.add(j.region);
    });
    contractors.forEach((c) => {
      if (c.branch) branches.add(c.branch);
      c.serviceRegions?.forEach((r) => regions.add(r));
    });
    return {
      branches: [...branches].sort(),
      regions: [...regions].sort(),
    };
  }, [jobs, contractors]);
}

export function BranchRegionFilter({
  value,
  onChange,
  jobs = [],
  contractors = [],
  branches: branchOverride,
  regions: regionOverride,
  compact = false,
}: BranchRegionFilterProps) {
  const derivedOptions = useBranchRegionOptions(jobs, contractors);
  const branches = branchOverride ?? derivedOptions.branches;
  const regions = regionOverride ?? derivedOptions.regions;

  if (branches.length === 0 && regions.length === 0) return null;

  const h = compact ? "h-7 text-[11px]" : "h-8 text-xs";

  return (
    <div className="flex items-center gap-2">
      {branches.length > 0 && (
        <Select value={value.branch} onValueChange={(v) => onChange({ ...value, branch: v })}>
          <SelectTrigger className={`${h} w-[140px] gap-1`}>
            <Building2 className="h-3 w-3 text-muted-foreground shrink-0" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Branches</SelectItem>
            {branches.map((b) => (
              <SelectItem key={b} value={b}>{b}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {regions.length > 0 && (
        <Select value={value.region} onValueChange={(v) => onChange({ ...value, region: v })}>
          <SelectTrigger className={`${h} w-[150px] gap-1`}>
            <MapPin className="h-3 w-3 text-muted-foreground shrink-0" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Regions</SelectItem>
            {regions.map((r) => (
              <SelectItem key={r} value={r}>{r}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

/** Utility to filter jobs by branch/region */
export function filterByBranchRegion<T extends { branch?: string; region?: string }>(
  items: T[],
  filter: BranchRegionState,
): T[] {
  return items.filter((item) => {
    if (filter.branch !== "all" && item.branch !== filter.branch) return false;
    if (filter.region !== "all" && item.region !== filter.region) return false;
    return true;
  });
}
